import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Mail, Phone, Globe, MapPin, ArrowLeft } from 'lucide-react';
import { database, ref, get, set } from '../../firebase';

const Dialog = ({ isOpen, onClose, title, message, type = 'info' }) => {
  if (!isOpen) return null;

  const bgColor = type === 'error' ? 'bg-red-500/10' : 'bg-green-500/10';
  const borderColor = type === 'error' ? 'border-red-500/30' : 'border-green-500/30';
  const Icon = type === 'error' ? '❌' : '✅';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className={`bg-card border ${borderColor} rounded-xl p-5 max-w-sm w-full`}>
        <div className="text-center">
          <div className={`inline-flex p-2.5 rounded-full ${bgColor} mb-3 text-lg`}>
            {Icon}
          </div>
          <h3 className="text-base font-bold text-foreground mb-1.5">{title}</h3>
          <p className="text-xs text-muted-foreground mb-4">{message}</p>
          <button
            onClick={onClose}
            className="btn-electric px-5 py-1.5 rounded-lg w-full text-xs"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

const EditProfile = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    country: '',
    email: '',
    city: '',
    region: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialog, setDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'info'
  });

  const countries = ['India', 'United States', 'United Kingdom', 'Canada', 'Australia'];

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const dbKey = localStorage.getItem('dbKey');
        const registrationDate = localStorage.getItem('registrationDate');
        
        if (!dbKey) {
          setDialog({
            isOpen: true,
            title: 'Error',
            message: 'User not logged in',
            type: 'error'
          });
          setTimeout(() => navigate('/login'), 1500);
          return;
        }

        let userRef;
        if (registrationDate) {
          userRef = ref(database, `users/${registrationDate}/${dbKey}`);
        } else {
          userRef = ref(database, dbKey);
        }

        const snapshot = await get(userRef);
        
        if (snapshot.exists()) {
          const userData = snapshot.val();
          setFormData({
            name: userData.name || '',
            mobile: userData.mobile || '',
            country: userData.country || 'India',
            email: userData.email || '',
            city: userData.city || '',
            region: userData.region || ''
          });
        } else {
          setDialog({
            isOpen: true,
            title: 'Error',
            message: 'User data not found',
            type: 'error'
          });
        }
      } catch (error) {
        console.error('Error:', error);
        setDialog({
          isOpen: true,
          title: 'Error',
          message: 'Failed to load profile',
          type: 'error'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name.trim()) {
      setDialog({
        isOpen: true,
        title: 'Validation Error',
        message: 'Please enter your full name',
        type: 'error'
      });
      return;
    }
    
    if (!formData.mobile.trim()) {
      setDialog({
        isOpen: true,
        title: 'Validation Error',
        message: 'Please enter mobile number',
        type: 'error'
      });
      return;
    }

    setSaving(true);
    
    try {
      const dbKey = localStorage.getItem('dbKey');
      const registrationDate = localStorage.getItem('registrationDate');
      
      if (!dbKey) {
        setDialog({
          isOpen: true,
          title: 'Error',
          message: 'Session expired',
          type: 'error'
        });
        return;
      }

      let userRef;
      if (registrationDate) {
        userRef = ref(database, `users/${registrationDate}/${dbKey}`);
      } else {
        userRef = ref(database, dbKey);
      }

      const snapshot = await get(userRef);
      
      if (!snapshot.exists()) {
        setDialog({
          isOpen: true,
          title: 'Error',
          message: 'User not found',
          type: 'error'
        });
        return;
      }

      const currentData = snapshot.val();
      const updatedData = {
        ...currentData,
        name: formData.name.trim(),
        mobile: formData.mobile,
        country: formData.country,
        city: formData.city,
        region: formData.region,
        updatedAt: new Date().toISOString()
      };

      await set(userRef, updatedData);

      // Update localStorage
      localStorage.setItem('userName', formData.name.trim());
      localStorage.setItem('userMobile', formData.mobile);
      localStorage.setItem('userCountry', formData.country);

      setDialog({
        isOpen: true,
        title: 'Success',
        message: 'Profile updated successfully',
        type: 'success'
      });

      setTimeout(() => navigate('/user-dashboard/profile'), 1500);

    } catch (error) {
      console.error('Error:', error);
      setDialog({
        isOpen: true,
        title: 'Error',
        message: 'Failed to update profile',
        type: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-4xl mx-auto px-2 pt-12 pb-4">
          <div className="flex items-center justify-center h-48">
            <div className="text-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto"></div>
              <p className="mt-2 text-[10px] text-muted-foreground">Loading...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Dialog
        isOpen={dialog.isOpen}
        onClose={() => setDialog({ ...dialog, isOpen: false })}
        title={dialog.title}
        message={dialog.message}
        type={dialog.type}
      />

      <div className="min-h-screen bg-background">
        <div className="w-full px-3 pt-2 pb-4">
          {/* Header */}
          <div className="flex items-center gap-2 mb-4">
            <button 
              onClick={() => navigate(-1)}
              className="p-1.5 hover:bg-secondary rounded-lg transition"
            >
              <ArrowLeft size={18} className="text-foreground" />
            </button>
            <div>
              <h1 className="text-base font-semibold text-foreground">Edit Profile</h1>
              <p className="text-xs text-muted-foreground mt-0.5">Update your information</p>
            </div>
          </div>

          {/* Form */}
          <div className="bg-card p-4 rounded-xl">
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Read-only Email */}
              <div>
                <label className="text-xs text-muted-foreground mb-1.5 flex items-center gap-1">
                  <Mail size={12} />
                  Email Address
                </label>
                <input 
                  type="email" 
                  value={formData.email}
                  readOnly
                  className="w-full bg-secondary text-muted-foreground text-sm px-4 py-3 rounded-lg border border-border"
                />
                <p className="text-[10px] text-muted-foreground mt-1">Email cannot be changed</p>
              </div>

              {/* Full Name */}
              <div>
                <label className="text-xs text-muted-foreground mb-1.5 flex items-center gap-1">
                  <User size={12} />
                  Full Name *
                </label>
                <input 
                  type="text" 
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full bg-background text-foreground text-sm px-4 py-3 rounded-lg border border-border focus:border-primary focus:outline-none"
                  placeholder="Enter your name"
                />
              </div>

              {/* Mobile */}
              <div>
                <label className="text-xs text-muted-foreground mb-1.5 flex items-center gap-1">
                  <Phone size={12} />
                  Mobile Number *
                </label>
                <input 
                  type="tel" 
                  name="mobile"
                  value={formData.mobile}
                  // onChange={handleChange}
                  required
                  className="w-full bg-background text-foreground text-sm px-4 py-3 rounded-lg border border-border focus:border-primary focus:outline-none"
                  placeholder="Enter mobile number"
                />
              </div>

              {/* Country */}
              <div>
                <label className="text-xs text-muted-foreground mb-1.5 flex items-center gap-1">
                  <Globe size={12} />
                  Country *
                </label>
                <select 
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  required
                  className="w-full bg-background text-foreground text-sm px-4 py-3 rounded-lg border border-border focus:border-primary focus:outline-none"
                >
                  {countries.map(country => (
                    <option key={country} value={country}>{country}</option>
                  ))}
                </select>
              </div>

              {/* City & Region */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground mb-1.5 flex items-center gap-1">
                    <MapPin size={12} />
                    City
                  </label>
                  <input 
                    type="text" 
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    className="w-full bg-background text-foreground text-sm px-4 py-3 rounded-lg border border-border focus:border-primary focus:outline-none"
                    placeholder="City"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1.5">
                    Region
                  </label>
                  <input 
                    type="text" 
                    name="region"
                    value={formData.region}
                    onChange={handleChange}
                    className="w-full bg-background text-foreground text-sm px-4 py-3 rounded-lg border border-border focus:border-primary focus:outline-none"
                    placeholder="Region/State"
                  />
                </div>
              </div>

              {/* Buttons */}
              <div className="flex gap-3 pt-3">
                <button 
                  type="submit" 
                  disabled={saving}
                  className="btn-electric py-3 px-5 rounded-lg flex-1 text-sm disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
                <button 
                  type="button"
                  onClick={() => navigate('/user-dashboard/profile')}
                  className="py-3 px-5 rounded-lg border border-border hover:bg-secondary transition flex-1 text-sm"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditProfile;